import React from 'react';

import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormLabel,
  Grid,
  GridProps,
  Radio,
  RadioGroup,
  Stack,
  styled
} from '@mui/material';
import {
  errorUtil,
  fields,
  Form,
  formUtil,
  validationUtil
} from 'iron-forge-react-modules';
import {
  Controller,
  useForm,
  useWatch
} from 'react-hook-form';

import { BaseWidget } from 'modules/layer01';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



export interface IProps {
  storeId: string;
}

const EnableWorkflowBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  background: '#FAFAFA',
  border: '1px solid #F7F7F8',
  borderRadius: 6,
  padding: '6px 12px',
  color: '#D8D8D8'
});

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.storeId);
  const [saveTriageWorkflows, saveTriageWorkflowsTuple] = dataService.useSaveTriageWorkflows(props.storeId);

  const form = useForm<types.IFormState>({
    defaultValues: {
      receivingWorkflow: {
        binId: '',
        name: ''
      },
      triageWorkflow: {
        binId: '',
        name: ''
      },
      replacementWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      repairWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      returnWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      creditWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      scrapWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      }
    }
  });

  formUtil.useSetFormFields(queryTuple, form.setValue, [
    'receivingWorkflow',
    'triageWorkflow',
    'replacementWorkflow',
    'repairWorkflow',
    'returnWorkflow',
    'creditWorkflow',
    'scrapWorkflow'
  ]);

  const formWatch = useWatch({ control: form.control });

  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      errorUtil.errorWrap(async () => {
        await saveTriageWorkflows(formState);
      }, {
        INVALID_ARGS: "Invalid arguments."
      });
    },
    [saveTriageWorkflows]
  );

  const defaultTextFieldProps = {
    disableDefaultMargins: true,
    isRequired: true,
    isDisabled: false,
    formControl: form.control
  };

  const defaultGridProps: GridProps = {
    item: true,
    xs: 12
  };

  const defaultHalfGridProps: GridProps = {
    ...defaultGridProps,
    md: 6
  };

  const loading = queryTuple.loading || saveTriageWorkflowsTuple.loading;
  return (
    <Form.FC
      disableDefaultOverflow
      onSubmit={form.handleSubmit(processSubmit)}>
      <BaseWidget.FC
        title="Triage Workflows"
        elAction={(
          <Button
            disabled={loading}
            type="submit">
            Save
          </Button>
        )}>

        <Grid container rowSpacing={2}>
          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}>
                <FormLabel>
                  Receiving
                </FormLabel>
              </Stack>
            </Grid>

            <Grid container spacing={1}>
              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  isDisabled={loading}
                  fieldKey="receivingWorkflow.name"
                  label="Name" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  isDisabled={loading}
                  extraValidationRules={{ pattern: validationUtil.reDecimalNumber }}
                  fieldKey="receivingWorkflow.binId"
                  label="NS Bin ID" />
              </Grid>
            </Grid>
          </>

          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}>
                <FormLabel>
                  Triage
                </FormLabel>
              </Stack>
            </Grid>

            <Grid container spacing={1}>
              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  isDisabled={loading}
                  fieldKey="triageWorkflow.name"
                  label="Name" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  isDisabled={loading}
                  fieldKey="triageWorkflow.binId"
                  label="NS Bin ID"
                  extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
              </Grid>
            </Grid>
          </>

          <Grid {...defaultGridProps}>
            <Divider />
          </Grid>

          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}>
                <FormLabel>
                  Replace
                </FormLabel>

                <Controller
                  control={form.control}
                  name="replacementWorkflow.enabled"
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}>
                      <FormControlLabel
                        disabled={loading}
                        value={true}
                        control={<Radio />}
                        label="Yes" />

                      <FormControlLabel
                        disabled={loading}
                        value={false}
                        control={<Radio />}
                        label="No" />
                    </RadioGroup>
                  )} />
              </Stack>
            </Grid>

            {formWatch.replacementWorkflow?.enabled === 'true' ? (
              <>
                <Grid container spacing={1}>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="replacementWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="replacementWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid {...defaultGridProps}>
                  <EnableWorkflowBox>
                    Enable Workflow to add bin
                  </EnableWorkflowBox>
                </Grid>
              </>
            )}
          </>

          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}>
                <FormLabel>
                  Repair
                </FormLabel>

                <Controller
                  control={form.control}
                  name="repairWorkflow.enabled"
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}>
                      <FormControlLabel
                        disabled={loading}
                        value={true}
                        control={<Radio />}
                        label="Yes" />

                      <FormControlLabel
                        disabled={loading}
                        value={false}
                        control={<Radio />}
                        label="No" />
                    </RadioGroup>
                  )} />
              </Stack>
            </Grid>

            {formWatch.repairWorkflow?.enabled === 'true' ? (
              <>
                <Grid container spacing={1}>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="repairWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="repairWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid {...defaultGridProps}>
                  <EnableWorkflowBox>
                    Enable Workflow to add bin
                  </EnableWorkflowBox>
                </Grid>
              </>
            )}
          </>

          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}>
                <FormLabel>
                  Return
                </FormLabel>

                <Controller
                  control={form.control}
                  name="returnWorkflow.enabled"
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}>
                      <FormControlLabel
                        disabled={loading}
                        value={true}
                        control={<Radio />}
                        label="Yes" />

                      <FormControlLabel
                        disabled={loading}
                        value={false}
                        control={<Radio />}
                        label="No" />
                    </RadioGroup>
                  )} />
              </Stack>
            </Grid>

            {formWatch.returnWorkflow?.enabled === 'true' ? (
              <>
                <Grid container spacing={1}>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="returnWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="returnWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid {...defaultGridProps}>
                  <EnableWorkflowBox>
                    Enable Workflow to add bin
                  </EnableWorkflowBox>
                </Grid>
              </>
            )}
          </>

          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}>
                <FormLabel>
                  Credit
                </FormLabel>

                <Controller
                  control={form.control}
                  name="creditWorkflow.enabled"
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}>
                      <FormControlLabel
                        disabled={loading}
                        value={true}
                        control={<Radio />}
                        label="Yes" />

                      <FormControlLabel
                        disabled={loading}
                        value={false}
                        control={<Radio />}
                        label="No" />
                    </RadioGroup>
                  )} />
              </Stack>
            </Grid>

            {formWatch.creditWorkflow?.enabled === 'true' ? (
              <>
                <Grid container spacing={1}>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="creditWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="creditWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid {...defaultGridProps}>
                  <EnableWorkflowBox>
                    Enable Workflow to add bin
                  </EnableWorkflowBox>
                </Grid>
              </>
            )}
          </>

          <>
            <Grid {...defaultGridProps}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}>
                <FormLabel>
                  Scrap
                </FormLabel>

                <Controller
                  control={form.control}
                  name="scrapWorkflow.enabled"
                  render={({ field }) => (
                    <RadioGroup
                      row
                      {...field}>
                      <FormControlLabel
                        disabled={loading}
                        value={true}
                        control={<Radio />}
                        label="Yes" />

                      <FormControlLabel
                        disabled={loading}
                        value={false}
                        control={<Radio />}
                        label="No" />
                    </RadioGroup>
                  )} />
              </Stack>
            </Grid>

            {formWatch.scrapWorkflow?.enabled === 'true' ? (
              <>
                <Grid container spacing={1}>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="scrapWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      isDisabled={loading}
                      fieldKey="scrapWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Grid {...defaultGridProps}>
                  <EnableWorkflowBox>
                    Enable Workflow to add bin
                  </EnableWorkflowBox>
                </Grid>
              </>
            )}
          </>
        </Grid>
      </BaseWidget.FC>
    </Form.FC>
  );
};
